import { Button, Radio, Stack } from '@mui/material';
import React, { useState } from 'react';
import { ChipWithCopy } from '../ChipWithCopy';
import LoadableMonacoEditor from 'js/components/elements/editors/monaco/LoadableMonacoEditor';
import { ReusableTransformType } from '@nexla/@types';
import Tooltip from 'js/components/elements/mui/Tooltip';
import { ReactComponent as LockIcon } from 'new/components/icons/fi-sr-lock.svg';
import { DetailsIconButton } from 'new/elements/Button';

type Option = {
  name: string;
  id: string | number;
  description: string;
};
type Props = {
  itemData: ReusableTransformType;
  checked: boolean;
  onSelect?: (o: Option) => void;
  onClickPreviewData(id: number): Promise<string>;
  onClickCard(data: Option | ReusableTransformType): void;
};

function SearchModalItem(props: Props) {
  const { itemData, checked, onSelect } = props;
  const [previewData, setPreviewData] = useState('');
  const [expand, onChangeExpand] = useState(false);
  const LoadableMonacoEditorOptions = { readOnly: true };

  async function handleOnClickPreview() {
    onChangeExpand((currentState) => !currentState);

    if (previewData !== '') return;

    try {
      const input = await props?.onClickPreviewData(Number(itemData.id));

      if (typeof input !== 'string') {
        console.error({
          componentName: 'SearchModalItem',
          message: 'Error: onClickPreview response does not match the expected type',
        });
        return;
      }

      setPreviewData(input);
    } catch (error) {
      console.error(error);
    }
  }

  const innerContainerCSS = expand
    ? 'search-item-inner-container no-bottom-border'
    : 'search-item-inner-container';

  const className = checked
    ? 'search-item checked-item-background'
    : 'search-item no-checked-item-background';

  const rowEndRender = (type: string) => {
    switch (type) {
      case 'transform':
        return (
          <Stack spacing="8px" direction="row" alignItems="center" gap={2}>
            <ChipWithCopy tooltipTitle="Copy Transform ID" toCopy={`${itemData.id}`}>
              ID {itemData.id}
            </ChipWithCopy>
            <DetailsIconButton
              className="panel-icon"
              onClick={() => props?.onClickCard(itemData)}
            />
          </Stack>
        );
      default:
        return (
          <>
            <Stack spacing="8px" direction="row" alignItems="center">
              <ChipWithCopy tooltipTitle="Copy Nexset ID" toCopy={`${itemData.id}`}>
                ID {itemData.id}
              </ChipWithCopy>
            </Stack>
            <Button variant="text" name="preview-sample" onClick={handleOnClickPreview}>
              {expand ? 'Close Preview' : 'Preview Sample Data'}
            </Button>
          </>
        );
    }
  };

  return (
    <li className={className} key={itemData.id}>
      <div className={innerContainerCSS}>
        {itemData && itemData?.access_roles && itemData?.access_roles.includes('collaborator') ? (
          <div className="lock">
            <Tooltip title="Do not have editing permissions" arrow={true}>
              <LockIcon />
            </Tooltip>
          </div>
        ) : (
          <div className="checkbox" onClick={() => onSelect(itemData)}>
            <Radio checked={checked} />
          </div>
        )}
        <div className="description">
          <span className="name">{itemData.name}</span>
          <p>{itemData.description}</p>
        </div>
        <div className="identify">{rowEndRender(itemData?.resource_type)}</div>
      </div>
      {expand ? (
        <div className="search-item-preview-container">
          <div className="loadable-monaco-editor">
            <LoadableMonacoEditor
              height={125}
              width="100%"
              theme="light"
              language="json"
              value={previewData}
              options={LoadableMonacoEditorOptions}
            />
          </div>
        </div>
      ) : null}
    </li>
  );
}

export default React.memo(SearchModalItem);
